import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { RippleButton } from "./RippleButton";
import { Menu, X } from "lucide-react";
import { useBooking } from "../context/BookingContext";

const LINKS = [
  { label: "About Us", path: "/about" },
  { label: "How It Works", id: "how", path: "/#how" },
  { label: "Services", id: "services", path: "/#services" },
  { label: "Why Woofsy", id: "why", path: "/#why" },
  { label: "Reviews", id: "reviews", path: "/#reviews" },
  { label: "FAQ", id: "faq", path: "/#faq" },
];

export default function Navbar({ onBook: customOnBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { openBookingModal } = useBooking();

  const handleBook = (serviceName = "", couponCode = "") => {
    if (customOnBook) {
      customOnBook(serviceName, couponCode);
    } else {
      openBookingModal(serviceName, couponCode);
    }
  };

  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.path === "/about" || link.path === "/promise") {
      navigate(link.path);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.getElementById(link.id)?.scrollIntoView({ behavior: "smooth" });
        }, 150);
      } else {
        document.getElementById(link.id)?.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <motion.header data-testid="navbar"
      initial={{ y: -80 }} animate={{ y: 0 }} transition={{ delay: 0.2, type: "spring", stiffness: 120 }}
      className="absolute top-0 inset-x-0 z-50 px-4 sm:px-8 pt-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between rounded-full px-5 sm:px-7 py-2.5 sm:py-3 glass shadow-md relative overflow-visible">
        <Link to="/" data-testid="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="relative flex items-center gap-2 font-heading font-extrabold text-2xl text-woofsy-dark shrink-0 overflow-visible z-10">
          <img src="/logo.png" alt="Woofsy Logo" className="h-16 sm:h-20 lg:h-24 w-auto object-contain -my-3 sm:-my-5 hover:scale-105 transition-transform origin-left filter drop-shadow-sm" />
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) => (
            <button key={l.label} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => go(l)}
              className={`text-sm font-medium transition-colors ${location.pathname === l.path ? "text-woofsy-primary font-bold" : "text-woofsy-dark/70 hover:text-woofsy-primary"}`}>
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <RippleButton data-testid="nav-book-btn" variant="primary" className="hidden sm:block px-5 py-2.5 text-sm" onClick={() => handleBook()}>Book Grooming</RippleButton>
          <button className="md:hidden p-2 text-woofsy-dark" data-testid="nav-menu-toggle" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="md:hidden glass mt-2 rounded-3xl p-5 max-w-7xl mx-auto flex flex-col gap-3">
          {LINKS.map((l) => (
            <button key={l.label} onClick={() => go(l)} className="text-left py-1 font-medium text-woofsy-dark">{l.label}</button>
          ))}
          <RippleButton variant="primary" className="px-5 py-2.5 text-sm mt-2" onClick={() => { setOpen(false); handleBook(); }}>Book Grooming</RippleButton>
        </motion.div>
      )}
    </motion.header>
  );
}
